import { Routes, Route, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setSession as setReduxSession, clearSession } from './store/slices/authSlice';
import { fetchAdminProfile, clearAdminProfile, fetchDepartments } from './store/slices/usersSlice';
import { RootState } from './store';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import notificationService, { ForegroundNotification } from './services/notificationService';
import { otpService } from './services/api';
import Breadcrumb from './components/Breadcrumb/Breadcrumb';
import Login from './components/auth/Login';

// Tabs
import CustomerDetailsPage from './components/sidenavbar/Users/CustomerDetails';
import MarketingOrdersTab from './components/sidenavbar/Marketing/MarketingOrdersTab';
import FarmOrdersTab from './components/sidenavbar/Marketing/FarmOrdersTab';
import CallsTab from './components/sidenavbar/Marketing/CallsTab';
import LeadsOverviewTab from './components/sidenavbar/Marketing/LeadsOverviewTab';
import TargetsOverviewTab from './components/sidenavbar/Marketing/TargetsOverviewTab';
import MarketingSummaryTab from './components/sidenavbar/Marketing/MarketingSummaryTab';
import UserPendingOrdersPage from './components/sidenavbar/Users/UserPendingOrdersPage';
import UserCreateOrderPage from './components/sidenavbar/Users/UserCreateOrderPage';
import UserAddPaymentPage from './components/sidenavbar/Users/UserAddPaymentPage';
import NetworkTab from './components/sidenavbar/Network/Network';
import NetworkUserDetailsPage from './components/sidenavbar/Network/NetworkUserDetails';
import { CONFIGURABLE_SCREENS, SCREENS_PENDING_ACCESS_CONFIG, effectiveScreenKey, screenForPath } from './config/screens';
import ACFHome from './components/sidenavbar/Acf/ACFHome';
import ACFUserDetails from './components/sidenavbar/Acf/ACFUserDetails';

// Public Pages
import ReferralLandingPage from './components/sidenavbar/public/ReferralLandingPage';
import LiveUnitsPage from './components/sidenavbar/public/LiveUnitsPage';
import DeactivateUserPage from './components/sidenavbar/public/DeactivateUserPage';
import AnimalKartCrmPage from './components/sidenavbar/public/AnimalKartCrmPage';
// /customer-kyc now gates into the secure investor profile; the old form (CustomerKycPage) is retired.
import KycLinkGate from './components/sidenavbar/public/KycLinkGate';
// Not lazy: the fallback shown while the unit calculator itself loads.
import CalculatorSkeleton from './components/sidenavbar/Calculators/Unit/CalculatorSkeleton';
import NomineeCorrectionPage from './components/sidenavbar/public/NomineeCorrectionPage';
import InvestorProfilePage from './components/sidenavbar/public/investorProfile/InvestorProfilePage';
import AssistedFillPage from './components/sidenavbar/KycQueue/AssistedFillPage';
import KycQueuePage from './components/sidenavbar/KycQueue/KycQueuePage';
import KycVerifyPage from './components/sidenavbar/KycQueue/KycVerifyPage';

// Redux
import { setHighlightedOrderId, setHighlightedMilestoneId, addNotification } from './store/slices/uiSlice';

// Privacy
import PrivacyPolicy from './components/sidenavbar/public/PrivacyPolicy';
import Support from './components/sidenavbar/public/Support';

// Skeletons
import OrdersPageSkeleton from './components/common/skeletons/OrdersPageSkeleton';
import UsersPageSkeleton from './components/common/skeletons/UsersPageSkeleton';
import ProductsPageSkeleton from './components/common/skeletons/ProductsPageSkeleton';
import EmiCalculatorSkeleton from './components/common/skeletons/EmiCalculatorSkeleton';

// Tabs (Lazy Loaded)
const OrdersTab = React.lazy(() => import('./components/sidenavbar/Orders/OrdersTab'));
const SessionOrderView = React.lazy(() => import('./components/sidenavbar/Orders/SessionOrderView'));
const UserDetails = React.lazy(() => import('./components/sidenavbar/Users/UserDetails'));
const ProductsTab = React.lazy(() => import('./components/sidenavbar/products/ProductsTab'));
const ProductDetailPage = React.lazy(() => import('./components/sidenavbar/products/ProductDetailPage'));
const EmiCalculatorTab = React.lazy(() => import('./components/sidenavbar/Calculators/Emi/EmiCalculatorTab'));
const AcfCalculatorTab = React.lazy(() => import('./components/sidenavbar/Calculators/Acf/AcfCalculatorTab'));
const UnitCalculatorTab = React.lazy(() => import('./components/sidenavbar/Calculators/Unit/UnitCalculatorTab'));
const ExitPlanCalculatorTab = React.lazy(() => import('./components/sidenavbar/Calculators/ExitPlan/ExitPlanCalculatorTab'));
const FarmManagement = React.lazy(() => import('./components/sidenavbar/Farm/FarmManagement'));
const SelfBenefitsTab = React.lazy(() => import('./components/sidenavbar/SelfBenefits/SelfBenefitsTab'));
const RoleRequestsTab = React.lazy(() => import('./components/sidenavbar/RoleRequests/RoleRequestsTab'));
const OffersAchievedTab = React.lazy(() => import('./components/sidenavbar/OffersAchieved/OffersAchievedTab'));
const RoasterTab = React.lazy(() => import('./components/sidenavbar/Roaster/RoasterTab'));
const AnalyticsTab = React.lazy(() => import('./components/sidenavbar/Analytics/AnalyticsTab'));
const AdminListTab = React.lazy(() => import('./components/sidenavbar/Users/AdminListTab'));
const StaffAccessTab = React.lazy(() => import('./components/sidenavbar/Staff/StaffAccessTab'));
const FarmManagementTab = React.lazy(() => import('./components/sidenavbar/FarmManagement/FarmManagementTab'));
const ProcurementBuffaloDetailsPage = React.lazy(() => import('./components/sidenavbar/FarmManagement/ProcurementBuffaloDetailsPage'));
const LeadDetailsPage = React.lazy(() => import('./components/sidenavbar/Leads/LeadDetailsPage'));
const AssetValuesTab = React.lazy(() => import('./components/sidenavbar/AssetValues/AssetValuesTab'));
const CashiersTab = React.lazy(() => import('./components/sidenavbar/Cashiers/CashiersTab'));
const CouponsTab = React.lazy(() => import('./components/sidenavbar/Coupons/CouponsTab'));
const LeaderboardTab = React.lazy(() => import('./components/sidenavbar/Marketing/LeaderboardTab'));
const SalesReportTab = React.lazy(() => import('./components/sidenavbar/Marketing/SalesReportTab'));
const VisitsTab = React.lazy(() => import('./components/sidenavbar/Marketing/VisitsTab'));
const IncentivesTab = React.lazy(() => import('./components/sidenavbar/Marketing/IncentivesTab'));
const FarmIncentivesTab = React.lazy(() => import('./components/sidenavbar/Marketing/FarmIncentivesTab'));
const FarmLeaderboardTab = React.lazy(() => import('./components/sidenavbar/Marketing/FarmLeaderboardTab'));

interface Session {
  mobile: string;
  role: string | null;
  name?: string;
  referralCode?: string;
  lastLoginTime?: string;
  currentLoginTime?: string;
}




// Smart redirect component removed as it is no longer needed

function App() {
  const postLoginRedirectRef = useRef<string | null>(null);
  const [session, setSession] = useState<Session | null>(() => {
    const saved = window.localStorage.getItem('ak_dashboard_session');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        window.localStorage.removeItem('ak_dashboard_session');
      }
    }
    return null;
  });
  const dispatch = useAppDispatch();
  const { adminProfile, adminProfileLoading, adminProfileError, departments, departmentsLoading } = useAppSelector((state: RootState) => state.users);
  const navigate = useNavigate();
  const location = useLocation();

  // Sync session stats to Redux exactly once when session is set/changed
  useEffect(() => {
    if (session) {
      dispatch(setReduxSession({
        adminMobile: session.mobile,
        adminName: session.name || 'Admin',
        adminRole: session.role || 'Admin',
        lastLogin: session.lastLoginTime || 'First Login',
        presentLogin: session.currentLoginTime || new Date().toLocaleString(),
        referralCode: session.referralCode || '',
      }));
    }
  }, [dispatch, session]); // Only re-run if session changes

  // Each toast has its own id so they stack independently
  type Toast = ForegroundNotification & { id: string };
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const dismissToast = useCallback((id: string) => {
    clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const pushToast = useCallback((payload: ForegroundNotification) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts(prev => [...prev, { ...payload, id }]);
    toastTimers.current[id] = setTimeout(() => dismissToast(id), 8000);
  }, [dismissToast]);

  // Fetch admin profile EXACTLY ONCE per session initialization
  const fetchStartedRef = useRef(false);
  useEffect(() => {
    if (session?.mobile && !adminProfile && !adminProfileLoading && !fetchStartedRef.current) {
      fetchStartedRef.current = true;
      dispatch(fetchAdminProfile(session.mobile));
    }
  }, [session?.mobile, adminProfile, adminProfileLoading, dispatch]);

  // Departments (with each one's head_role) are needed to know whether the
  // logged-in user is a department head — both the sidebar nav gate and the
  // /staff-access route guard read this from Redux rather than each fetching
  // it themselves.
  const departmentsFetchStartedRef = useRef(false);
  useEffect(() => {
    if (session?.mobile && departments.length === 0 && !departmentsLoading && !departmentsFetchStartedRef.current) {
      departmentsFetchStartedRef.current = true;
      dispatch(fetchDepartments());
    }
  }, [session?.mobile, departments.length, departmentsLoading, dispatch]);

  // Wire up FCM: register SW, get/save token, subscribe to admin topic,
  // and listen for foreground and background messages.
  useEffect(() => {
    if (!session?.mobile) return;

    const roles = session.role ? session.role.split(',').map((r) => r.trim()) : [];

    // Foreground listener: store in Redux, show stacked toast, navigate + highlight
    const unsubscribeMessage = notificationService.onForegroundMessage((payload) => {
      dispatch(addNotification({ title: payload.title, body: payload.body, data: payload.data }));
      pushToast(payload);
    });

    // Init FCM, save token, subscribe to admin topic
    notificationService.onUserLogin(session.mobile, roles);

    // Handle clicks on background OS notifications (posted by the service worker)
    const handleSwMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'FCM_NOTIFICATION_CLICK') return;
      const url: string = event.data.url || '/orders';
      const urlObj = new URL(url, window.location.origin);
      const highlightOrder = urlObj.searchParams.get('highlight_order');
      const highlightMilestone = urlObj.searchParams.get('highlight_milestone');
      if (highlightOrder) dispatch(setHighlightedOrderId(highlightOrder));
      if (highlightMilestone) dispatch(setHighlightedMilestoneId(highlightMilestone));
      navigate(urlObj.pathname);
    };

    navigator.serviceWorker?.addEventListener('message', handleSwMessage);

    return () => {
      unsubscribeMessage();
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.mobile]);

  // Parse highlight query-params on cold open (tab opened by SW on notification click)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const highlightOrder = params.get('highlight_order');
    const highlightMilestone = params.get('highlight_milestone');
    if (highlightOrder) dispatch(setHighlightedOrderId(highlightOrder));
    if (highlightMilestone) dispatch(setHighlightedMilestoneId(highlightMilestone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleLogin = useCallback((s: Session) => {
    // Determine last login (from previous session or current if new)
    const prevSessionStr = window.localStorage.getItem('ak_dashboard_session');
    let lastLoginTime = new Date().toLocaleString();

    if (prevSessionStr) {
      try {
        const prevSession = JSON.parse(prevSessionStr);
        if (prevSession.currentLoginTime) {
          lastLoginTime = prevSession.currentLoginTime;
        }
      } catch (e) { }
    }

    const newSession = {
      ...s,
      currentLoginTime: new Date().toLocaleString(),
      lastLoginTime: lastLoginTime
    };

    window.localStorage.setItem('ak_dashboard_session', JSON.stringify(newSession));

    // Determine redirect target BEFORE triggering state updates so the ref is
    // ready when the login route re-renders (v7_startTransition makes navigate()
    // a low-priority transition that can be overridden by the urgent setSession
    // re-render, so we use a ref instead).
    const storedRedirect = window.localStorage.getItem('post_login_redirect');
    if (storedRedirect) {
      window.localStorage.removeItem('post_login_redirect');
      postLoginRedirectRef.current = storedRedirect;
    } else {
      const fromState = (location.state as any)?.from;
      const fromPath = fromState?.pathname;
      const fromSearch = fromState?.search || '';
      postLoginRedirectRef.current = (fromPath && fromPath !== '/login') ? `${fromPath}${fromSearch}` : '/orders';
    }

    setSession(newSession);
    dispatch(setReduxSession({
      adminMobile: newSession.mobile,
      adminName: newSession.name || 'Admin',
      adminRole: newSession.role || 'Admin',
      lastLogin: newSession.lastLoginTime || 'First Login',
      presentLogin: newSession.currentLoginTime || new Date().toLocaleString(),
      referralCode: newSession.referralCode || '',
    }));
  }, [dispatch, location.state]);

  const handleLogout = async () => {
    if (session?.mobile) {
      const roles = session.role ? session.role.split(',').map((r) => r.trim()) : [];
      notificationService.onUserLogout(session.mobile, roles);
      // Server-side token invalidation — call this while the token is still
      // in storage (so it authenticates the request) and before clearing
      // everything below. Best-effort: otpService.logout never throws, so a
      // failed call still falls through to the local clear.
      await otpService.logout(session.mobile);
    }
    // Clear Redux state
    dispatch(clearSession());
    dispatch(clearAdminProfile());
    // Reset fetch flags for next login
    fetchStartedRef.current = false;
    departmentsFetchStartedRef.current = false;

    // Clear ALL local storage for security and a clean state — this is what
    // actually removes the access token (see utils/authToken.ts) on this device.
    window.localStorage.clear();
    setSession(null);
    navigate('/login', { replace: true });
  };

  // services/api.ts dispatches this on any 401 (missing/invalid/expired JWT)
  // — bounce to login the same way an explicit logout does, since the stale
  // token it already cleared can't be refreshed without a new OTP verify.
  useEffect(() => {
    window.addEventListener('auth:unauthorized', handleLogout);
    return () => window.removeEventListener('auth:unauthorized', handleLogout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.mobile]);



  return (
    <div className="App">
      {/* Stacked foreground notification toasts — bottom-right column */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9997,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: '360px',
        width: '360px',
        pointerEvents: toasts.length === 0 ? 'none' : 'auto',
      }}>
        {toasts.map((toast) => {
          const hasLink = !!(toast.data?.order_id || toast.data?.milestone_id || toast.data?.recipient_mobile);
          const handleView = () => {
            const data = toast.data || {};
            if (data.type === 'MILESTONE_ACHIEVED' && data.milestone_id) {
              dispatch(setHighlightedMilestoneId(data.milestone_id));
              navigate('/offer-settings');
            } else if (data.type === 'REFERRAL_REWARD' && data.recipient_mobile) {
              navigate(`/user-management/network/${data.recipient_mobile}`);
            } else if (data.order_id) {
              dispatch(setHighlightedOrderId(data.order_id));
              navigate('/orders');
            }
            dismissToast(toast.id);
          };
          return (
            <div key={toast.id} style={{
              backgroundColor: '#1e293b',
              color: 'white',
              padding: '14px 16px',
              borderRadius: '10px',
              boxShadow: '0 10px 25px -3px rgba(0,0,0,0.5)',
              border: '1px solid rgba(99,102,241,0.25)',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              animation: 'slideIn 0.25s ease-out',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ fontWeight: 600, fontSize: '13px', lineHeight: '1.4', flex: 1 }}>{toast.title}</span>
                <button
                  onClick={() => dismissToast(toast.id)}
                  style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0', flexShrink: 0 }}>
                  ✕
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5', color: '#94a3b8' }}>{toast.body}</p>
              {hasLink && (
                <button
                  onClick={handleView}
                  style={{
                    marginTop: '6px',
                    alignSelf: 'flex-end',
                    background: 'none',
                    border: '1px solid rgba(99,102,241,0.4)',
                    color: '#818cf8',
                    cursor: 'pointer',
                    padding: '3px 12px',
                    borderRadius: '4px',
                    fontSize: '11px',
                  }}>
                  View →
                </button>
              )}
            </div>
          );
        })}
      </div>

      <Routes>
        <Route path="/login" element={
          session ? <Navigate to={postLoginRedirectRef.current || '/orders'} replace /> : <Login onLogin={handleLogin} />
        } />

        {/* Fully public standalone route — no auth, no layout wrapper */}
        <Route path="/customer-kyc" element={<KycLinkGate />} />
        <Route path="/nominee-correction" element={<NomineeCorrectionPage />} />
        {/* Investor KYC / bank / unit-nominee flow, opened from the tokenised link */}
        <Route path="/investor-profile" element={<InvestorProfilePage />} />

        {/* Persistent Layout for all authenticated dashboard views */}
        <Route element={<DashboardLayout session={session} handleLogout={handleLogout} />}>

          {/* Strictly Protected Routes */}
          <Route element={<RequireAuth session={session} adminProfile={adminProfile} adminProfileLoading={adminProfileLoading} adminProfileError={adminProfileError} departments={departments} handleLogout={handleLogout}><Outlet /></RequireAuth>}>

            <Route path="/orders" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <OrdersTab />
              </React.Suspense>
            } />

            <Route path="/orders/:orderId" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <SessionOrderView />
              </React.Suspense>
            } />

            <Route path="/user-management" element={
              <React.Suspense fallback={<UsersPageSkeleton />}>
                <UserDetails />
              </React.Suspense>
            } />

            <Route path="/analytics" element={
              <React.Suspense fallback={<UsersPageSkeleton />}>
                <AnalyticsTab />
              </React.Suspense>
            } />

            <Route path="/admin-list" element={
              <React.Suspense fallback={<UsersPageSkeleton />}>
                <AdminListTab />
              </React.Suspense>
            } />

            <Route path="/staff-access" element={
              <React.Suspense fallback={<UsersPageSkeleton />}>
                <StaffAccessTab />
              </React.Suspense>
            } />

            <Route path="/farm-procurement" element={
              <React.Suspense fallback={<UsersPageSkeleton />}>
                <FarmManagementTab />
              </React.Suspense>
            } />
            <Route path="/farm-procurement/:buffaloId" element={
              <React.Suspense fallback={<UsersPageSkeleton />}>
                <ProcurementBuffaloDetailsPage />
              </React.Suspense>
            } />

            <Route path="/marketing" element={<Navigate to="/marketing/sales-report" replace />} />
            <Route path="/marketing/sales-report" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <SalesReportTab />
              </React.Suspense>
            } />
            <Route path="/marketing/leaderboard" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <LeaderboardTab />
              </React.Suspense>
            } />
            <Route path="/marketing/my-orders" element={<MarketingOrdersTab />} />
            <Route path="/marketing/farm-orders" element={<FarmOrdersTab />} />
            <Route path="/marketing/incentive-approvals" element={<Navigate to="/marketing/my-orders" replace />} />
            <Route path="/marketing/calls" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <CallsTab />
              </React.Suspense>
            } />
            <Route path="/marketing/visits" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <VisitsTab />
              </React.Suspense>
            } />
            <Route path="/marketing/leads" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <LeadsOverviewTab />
              </React.Suspense>
            } />
            <Route path="/leads/:leadId" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <LeadDetailsPage />
              </React.Suspense>
            } />
            <Route path="/marketing/targets" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <TargetsOverviewTab />
              </React.Suspense>
            } />
            <Route path="/marketing/overview" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <MarketingSummaryTab />
              </React.Suspense>
            } />
            <Route path="/marketing/incentives" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <IncentivesTab />
              </React.Suspense>
            } />
            <Route path="/marketing/farm-incentives" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <FarmIncentivesTab />
              </React.Suspense>
            } />
            <Route path="/marketing/farm-leaderboard" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <FarmLeaderboardTab />
              </React.Suspense>
            } />
            {/* Investor KYC / bank / unit-nominee verification */}
            <Route path="/kyc-queue" element={<KycQueuePage />} />
            <Route path="/kyc-queue/:mobile" element={<KycVerifyPage />} />
            <Route path="/kyc-queue/:mobile/fill" element={<AssistedFillPage />} />
            <Route path="/users/customers/:mobile" element={<CustomerDetailsPage />} />
            <Route path="/users/customers/:mobile/pending-orders" element={<UserPendingOrdersPage />} />
            <Route path="/users/customers/:mobile/create-order" element={<UserCreateOrderPage />} />
            <Route path="/users/customers/:mobile/orders/:orderId/add-payment" element={<UserAddPaymentPage />} />

            <Route path="/products" element={
              <React.Suspense fallback={<ProductsPageSkeleton />}>
                <ProductsTab />
              </React.Suspense>
            } />

            <Route path="/products/:productId" element={
              <React.Suspense fallback={<ProductsPageSkeleton />}>
                <ProductDetailPage />
              </React.Suspense>
            } />

            <Route path="/user-management/network" element={<NetworkTab />} />
            <Route path="/user-management/network/:mobile" element={<NetworkUserDetailsPage />} />

            <Route path="/profile/:mobile" element={<NetworkUserDetailsPage />} />

            <Route path="/acf" element={<ACFHome />} />
            <Route path="/acf/details/:userId/:orderId" element={<ACFUserDetails />} />


            <Route path="/farm-management" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <FarmManagement />
              </React.Suspense>
            } />
            <Route path="/offer-settings" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <SelfBenefitsTab />
              </React.Suspense>
            } />
            <Route path="/coupons" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <CouponsTab />
              </React.Suspense>
            } />
            <Route path="/offers-achieved" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <OffersAchievedTab />
              </React.Suspense>
            } />
            <Route path="/role-requests" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <RoleRequestsTab />
              </React.Suspense>
            } />
            <Route path="/roaster" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <RoasterTab />
              </React.Suspense>
            } />
            <Route path="/asset-values" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <AssetValuesTab />
              </React.Suspense>
            } />
            {/* Screen-access is enforced generically: 'cashiers' is in
                CONFIGURABLE_SCREENS, so screenForPath in the guard above
                already denies a staff member who was not granted it. */}
            <Route path="/cashiers" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <CashiersTab />
              </React.Suspense>
            } />
            {/* Not a configurable screen key: the navbar shows it to Admin /
                SuperAdmin, and GET /payouts/due-details enforces dashboard
                staff roles itself. */}

          </Route>

          {/* Public/Hybrid Routes - Layout visible if logged in */}


          <Route path="/unit-calculator" element={
            <React.Suspense fallback={<CalculatorSkeleton />}>
              <UnitCalculatorTab tree={true} />
            </React.Suspense>
          } />

          <Route path="/unit-calculator/:modelVersion" element={
            <React.Suspense fallback={<CalculatorSkeleton />}>
              <UnitCalculatorTab tree={true} />
            </React.Suspense>
          } />

          <Route path="/unit-plan-calculator" element={
            <React.Suspense fallback={<OrdersPageSkeleton />}>
              <ExitPlanCalculatorTab />
            </React.Suspense>
          } />

          <Route path="/emi-calculator" element={
            <React.Suspense fallback={<EmiCalculatorSkeleton />}>
              <EmiCalculatorTab />
            </React.Suspense>
          } />

          <Route path="/emi-calculator/:modelVersion" element={
            <React.Suspense fallback={<EmiCalculatorSkeleton />}>
              <EmiCalculatorTab />
            </React.Suspense>
          } />

          <Route path="/acf-calculator" element={
            <React.Suspense fallback={<EmiCalculatorSkeleton />}>
              <AcfCalculatorTab />
            </React.Suspense>
          } />

          <Route path="/acf-calculator/:modelVersion" element={
            <React.Suspense fallback={<EmiCalculatorSkeleton />}>
              <AcfCalculatorTab />
            </React.Suspense>
          } />

          <Route path="/referral-landing" element={<ReferralLandingPage />} />
          <Route path="/units-live" element={<LiveUnitsPage />} />
          <Route path="/deactivate-user" element={<DeactivateUserPage />} />
          <Route path="/animalkart-crm" element={<AnimalKartCrmPage />} />
          <Route path="/crm-download" element={<Navigate to="/animalkart-crm" replace />} />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />


        </Route>

        {/* Backward Compatibility Redirects */}
        <Route path="/dashboard/orders" element={<Navigate to="/orders" replace />} />
        <Route path="/dashboard/user-management" element={<Navigate to="/user-management" replace />} />
        <Route path="/dashboard/products" element={<Navigate to="/products" replace />} />

        <Route path="/dashboard/emi-calculator" element={<Navigate to="/emi-calculator" replace />} />
        <Route path="/dashboard/acf-calculator" element={<Navigate to="/acf-calculator" replace />} />
        <Route path="/dashboard/*" element={<Navigate to="/orders" replace />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to={session ? "/orders" : "/login"} replace />} />
        <Route path="*" element={<Navigate to={session ? "/orders" : "/login"} replace />} />
      </Routes>
    </div>
  );
}

const DashboardLayout = ({ session, handleLogout }: { session: Session | null, handleLogout: () => void }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isSessionView = !!searchParams.get('session');

  // Define routes that should ALWAYS show the sidebar (protected dashboard content)
  const protectedPrefixes = [
    '/orders',
    '/user-management',
    '/users/customers',
    '/kyc-queue',
    '/products',
    '/acf',

    '/farm-management',
    '/farm-procurement',
    '/offer-settings',
    '/coupons',
    '/role-requests',
    '/offers-achieved',
    '/roaster',
    '/analytics',
    '/admin-list',
    '/staff-access',
    '/asset-values',
    '/cashiers',
    '/first-due-payouts',
    '/profile',
    '/marketing',
    '/leads'
  ];

  const isProtectedPath = protectedPrefixes.some(prefix => location.pathname.startsWith(prefix)) && !location.pathname.startsWith('/acf-calculator');

  // If the user navigates from the dashboard (via sidebar), state.fromDashboard will be true
  const isFromDashboard = !!(location.state as any)?.fromDashboard;

  // Hybrid paths are public pages that CAN show the dashboard layout if accessed from within
  const hybridPrefixes = [

    '/support',
    '/privacy-policy',
    '/deactivate-user',
    '/unit-calculator',
    '/unit-plan-calculator',
    '/emi-calculator',
    '/acf-calculator',
    '/referral-landing',
    '/units-live',
    '/animalkart-crm',
    '/crm-download',
  ];
  const isHybridPath = hybridPrefixes.some(prefix => location.pathname.startsWith(prefix));

  // We show the dashboard layout IF:
  // 1. It's a core protected dashboard route
  // 2. OR it's a hybrid route AND we are coming from the dashboard / have an active session
  const showDashboardLayout = isProtectedPath || (isHybridPath && (isFromDashboard || !!session));

  // Standalone mode is for public/hybrid pages accessed directly via URL or when no dashboard context is present
  const isStandalone = !showDashboardLayout;

  if (isStandalone) {
    return (
      <main className="h-screen w-full overflow-y-auto bg-slate-100">
        <Outlet />
      </main>
    );
  }

  return (
    <Breadcrumb
      adminMobile={session?.mobile}
      adminName={session?.name}
      adminRole={session?.role || undefined}
      lastLogin={session?.lastLoginTime}
      presentLogin={session?.currentLoginTime}
      onLogout={handleLogout}
      isSessionView={isSessionView}
    >
      <Outlet />
    </Breadcrumb>
  );
};

// Roles that predate the screen_access feature — they still default to full/legacy
// dashboard access when unconfigured (hasScreenConfig false), for backward compatibility.
const LEGACY_DEFAULT_ROLES = ['admin', 'animalkart admin', 'employee', 'supervisor', 'cashier'];

// Helper for strict protection
const RequireAuth = ({ children, session, adminProfile, adminProfileLoading, adminProfileError, departments, handleLogout }: { children: React.ReactNode, session: Session | null, adminProfile: any, adminProfileLoading: boolean, adminProfileError: string | null, departments: any[], handleLogout: () => void }) => {
  const location = useLocation();
  if (!session) {
    // Persist the full session link so it survives page reloads during login
    const sessionToken = new URLSearchParams(location.search).get('session');
    if (sessionToken) {
      window.localStorage.setItem('post_login_redirect', `${location.pathname}${location.search}`);
    }
    return <Navigate to="/login" replace state={{ from: { pathname: location.pathname, search: location.search } }} />;
  }

  // If profile is loading OR we have a session but haven't started/finished fetching the profile yet, wait.
  // We stop waiting if an error occurs so we don't hang indefinitely. This must run BEFORE the
  // authorization gate below, since that gate needs adminProfile.screen_access to decide dynamically.
  if ((adminProfileLoading || (session && !adminProfile)) && !adminProfileError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Rolecheck
  const sessionRoles = session.role ? session.role.split(',').map((r: string) => r.trim().toLowerCase()) : [];
  const profileRoles = adminProfile?.role ? adminProfile.role.split(',').map((r: string) => r.trim().toLowerCase()) : [];
  // For strict routes, allow superadmin. If not superadmin, check if it's admin or employee and enforce boundaries
  const isSuperAdmin = sessionRoles.some((r: string) => r.includes('superadmin')) || profileRoles.some((r: string) => r.includes('superadmin'));
  const hasAdmin = sessionRoles.some((r: string) => r.includes('admin')) || profileRoles.some((r: string) => r.includes('admin'));

  // Screen-access enforcement is active only when the backend returned a
  // NON-EMPTY screen_access map for this staff user. Otherwise (unconfigured
  // staff, or a backend that predates the feature) we fall back to the original
  // role-based rules so nobody is ever locked out to /support.
  const access: Record<string, string> = adminProfile?.screen_access || {};
  const hasScreenConfig = Object.keys(access).length > 0;

  // Dashboard entry is authorized dynamically off whatever the backend actually granted:
  // SuperAdmin is always unrestricted; anyone else who has been handed a non-empty
  // screen_access map by an admin is authorized regardless of their role name (so new
  // roles — Marketing Manager/Lead/Executive, or anything added later — work without
  // this list needing to know about them); legacy roles keep their pre-screen_access
  // default access until a SuperAdmin configures them.
  const isLegacyRole = sessionRoles.some((r: string) => LEGACY_DEFAULT_ROLES.includes(r)) || profileRoles.some((r: string) => LEGACY_DEFAULT_ROLES.includes(r));
  const isAuthorized = isSuperAdmin || hasScreenConfig || isLegacyRole;

  if (!isAuthorized) {
    return (
      <div style={{ maxWidth: 600, margin: '2rem auto', padding: '1.5rem', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        <h2 style={{ marginBottom: '0.75rem' }}>Access Restricted</h2>
        <p style={{ marginBottom: 0 }}>Only authorized users can access this dashboard. Please login with an authorized mobile.</p>
        <button onClick={handleLogout} style={{ marginTop: '1rem', padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Logout</button>
      </div>
    );
  }

  // Everyone except SuperAdmin is configurable — Employee/Supervisor/Cashier AND
  // plain Admins. Graceful degradation (hasScreenConfig) keeps an unconfigured
  // user on their legacy access until the SuperAdmin grants them a screen map.
  const isConfigurableStaff = !isSuperAdmin;
  const isEmployee = sessionRoles.some((r: string) => r.includes('employee')) || profileRoles.some((r: string) => r.includes('employee'));
  const isSupervisor = sessionRoles.some((r: string) => r.includes('supervisor')) || profileRoles.some((r: string) => r.includes('supervisor'));

  // Staff Access screen is open to anyone who can manage staff: SuperAdmin,
  // Admin, Supervisor — plus a department head (their role exactly matches
  // some department's head_role), who StaffAccessTab itself then locks to
  // only their own department.
  const isDepartmentHead = departments.some((d: any) => {
    const headRole = (d?.head_role || '').trim().toLowerCase();
    return headRole !== '' && (sessionRoles.includes(headRole) || profileRoles.includes(headRole));
  });
  if (location.pathname.startsWith('/staff-access')) {
    return (isSuperAdmin || hasAdmin || isSupervisor || isDepartmentHead) ? <>{children}</> : <Navigate to="/orders" replace />;
  }

  // Analytics is a privileged-only screen (SuperAdmin + Admin). It is NOT part of the
  // configurable screen catalog, so gate it explicitly here — a configured Admin still
  // sees it regardless of their screen_access map.
  if (location.pathname.startsWith('/analytics')) {
    return (isSuperAdmin || hasAdmin) ? <>{children}</> : <Navigate to="/orders" replace />;
  }

  if (isConfigurableStaff && hasScreenConfig) {
    // Route-level screen-access enforcement for configured staff (mirrors SideNavbar).
    // A screen added after this user's access map was saved has no entry, and
    // treating that as "denied" redirects them away from a page nobody decided
    // to deny them. Falls back to allowed for those keys only; anything with an
    // explicit entry is obeyed exactly. Mirrors showNewScreen in SideNavbar —
    // a nav item that is visible but redirects on click is worse than either.
    const hasAccess = (screenKey: string) => {
      const key = effectiveScreenKey(access, screenKey);
      return access[key] === 'read' || access[key] === 'write'
        || (SCREENS_PENDING_ACCESS_CONFIG.has(key) && !(key in access));
    };
    const firstGranted = CONFIGURABLE_SCREENS.find(s => hasAccess(s.key));
    const fallbackRoute = firstGranted ? firstGranted.routes[0] : '/support';

    // SuperAdmin-only screens are never grantable to staff. Farm Mgmt and Farm
    // Procurement dropped out of the configurable screen catalog entirely, so
    // they must be listed here explicitly or a configured staff member's
    // screen_access map would no longer deny them via screenForPath.
    const superAdminOnly = ['/true-harvest', '/admin-list', '/farm-management', '/farm-procurement'];
    if (superAdminOnly.some(prefix => location.pathname.startsWith(prefix))) {
      return <Navigate to={fallbackRoute} replace />;
    }
    // If the path maps to a configurable screen the user was not granted, redirect.
    const screen = screenForPath(location.pathname);
    if (screen && !hasAccess(screen.key)) {
      return <Navigate to={fallbackRoute} replace />;
    }
  } else {
    // Legacy role-based restrictions (privileged users, or unconfigured staff).
    if (!isSuperAdmin) {
      const restrictedPrefixes = ['/true-harvest', '/farm-management', '/farm-procurement', '/admin-list', '/offer-settings', '/coupons', '/asset-values'];
      if (restrictedPrefixes.some(prefix => location.pathname.startsWith(prefix))) {
        // Carve-out: Admins and Employees may enter /coupons.
        if (!location.pathname.startsWith('/coupons') || (!isEmployee && !hasAdmin)) {
          return <Navigate to="/orders" replace />;
        }
      }
    }
    if (isConfigurableStaff && isEmployee) {
      const employeeRestrictedPrefixes = ['/role-requests', '/offers-achieved', '/roaster', '/user-management/network'];
      if (employeeRestrictedPrefixes.some(prefix => location.pathname.startsWith(prefix))) {
        return <Navigate to="/orders" replace />;
      }
    }
  }

  return <>{children}</>;
};



export default App;
